import { useTranslator } from '@bluebeam/react-i18n';
import { Button } from 'components/lib';
import { ModalStaticProperties } from 'components/ModalPortal/ModalPortal';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { useContext, useState } from 'react';
import { Sheet } from 'records/Sheet';
import MapDrawingSelectionSubHeader from '../MapDrawingSelectionModal/MapDrawingSelectionSubHeader';
import ModalStdHeader from '../ModalStdHeader';
import ModalGrid from './ModalGrid';
import ModalGridOption from './ModalGridOption';

type Props = {
  onLayerSelect: (sheetid: string, pageid: string) => void;
  subText: string;
} & ModalStaticProperties;

const SelectVisibleLayersModal: React.FC<Props> & ModalStaticProperties = (
  props: Props
) => {
  const { onLayerSelect, subText } = props;
  const { state: appState, actions } = useContext(AppStoreContext);
  const [selectedLayer, setSelectedLayer] = useState<Sheet | null>(null);
  const { translate } = useTranslator(['mapView', 'buttons']);

  const visibleSheets = appState
    .getVisibleSheetEntries()
    .map((entry) => entry.sheet)
    .valueSeq()
    .toArray();

  const handleLayerClick = () => {
    const latestSheetVersion = selectedLayer?.versions.last(null);
    if (latestSheetVersion) {
      const { pageid, sheetid } = latestSheetVersion;

      onLayerSelect(sheetid, pageid);
    }
  };

  return (
    <div
      id="select-visible-layer-modal"
      className="modal__content"
      style={{ height: '84vh' }}
    >
      <ModalStdHeader
        text={translate('mapView:selectVisibleLayersModal.selectDrawing')}
      />
      <div className="h100 pt7 pb6">
        <div className="h100 column mt5">
          <div className="mx4">
            <div className="font-size-7 medium mb2">
              {translate(
                'mapView:selectVisibleLayersModal.selectDrawingFromLayers'
              )}
            </div>
            <div className="font-size-5 light mb4">{subText}</div>
            <div className="bb-2 mb4" />
          </div>
          <MapDrawingSelectionSubHeader sheetsLength={visibleSheets.length} />
          <div
            className="grow-1 oa bgd-mid-grey-1 mx4 bt-2 br-2 bl-2"
            style={{
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
            }}
          >
            <ModalGrid
              gridGap="16px"
              gridTemplateColumns="repeat(3, 172px)"
              gridTemplateRows="repeat(auto-fill, 175px)"
              height="calc(100vh - 550px)"
            >
              {visibleSheets.map((sheet) => (
                <ModalGridOption
                  key={sheet.sheetid}
                  selected={selectedLayer?.sheetid === sheet.sheetid}
                  sheet={sheet}
                  onOptionClick={() => setSelectedLayer(sheet)}
                />
              ))}
            </ModalGrid>
          </div>

          <div className="row shrink-0 justify-between px4 py3 bt-2">
            <Button
              id="map-select-layer-cancel-btn"
              size="large"
              variant="tertiary"
              onClick={() => actions.clearAllModals()}
            >
              {translate('buttons:cancel')}
            </Button>
            <Button
              id="map-select-layer-confirm-btn"
              disabled={selectedLayer === null}
              size="large"
              variant="primary"
              onClick={() => handleLayerClick()}
            >
              {translate('buttons:select')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

SelectVisibleLayersModal.modalClasses = {
  overlay: `modal__overlay`,
  content: `modal__window modal--sm`,
};

SelectVisibleLayersModal.modalStyles = {
  content: {
    width: '628px',
  },
};

export default SelectVisibleLayersModal;
